import { useRef, useEffect, useState, useCallback } from "react"
import { api } from "apiServer"
import { getAccessToken } from "lib/Cookies"

// ─── Constants ───────────────────────────────────────────────────────────────
const PAGE_SIZE = 24
const PAGE_NUM = 1
const DEBOUNCE_MS = 300
const REQUEST_TIMEOUT_MS = 10000

// ─── Custom hook: useTopicData ────────────────────────────────────────────────
/**
 * Manages all data-fetching logic for the topic page:
 * - SSR hydration (skip first fetch when SSR data is present)
 * - Category tab switching (debounced, resets list)
 * - Infinite scroll pagination
 * - AbortController per request (cancels in-flight on unmount / new request)
 * - isFetchingRef guard (prevents duplicate concurrent requests)
 */
export function useTopicData(id, initialTopic) {
  const [topicMeta, setTopicMeta] = useState(initialTopic)
  const [activeCategory, setActiveCategory] = useState(null)
  const [items, setItems] = useState(initialTopic?.videos || [])
  const [hasMore, setHasMore] = useState((initialTopic?.videos?.length || 0) >= PAGE_SIZE)
  const [loading, setLoading] = useState(false)
  const [pageNum, setPageNum] = useState(PAGE_NUM)

  const [categories, setCategories] = useState(initialTopic?.categories || [])

  // Refs — mutable, never trigger re-render
  const abortRef = useRef(null)
  const isFetchingRef = useRef(false)
  const isSSRHydratedRef = useRef(false) // true after SSR data has been "consumed"
  const debounceRef = useRef(null)
  const activeCategoryRef = useRef(null) // mirror of activeCategory for use inside callbacks

  // Keep ref in sync so fetchPage closure always reads current category
  activeCategoryRef.current = activeCategory

  // ── Core fetch ──────────────────────────────────────────────────────────────
  const fetchPage = useCallback(
    async (page, categoryId) => {
      // Guard: one request at a time
      if (isFetchingRef.current) return

      // Cancel any in-flight request
      abortRef.current?.abort()
      abortRef.current = new AbortController()

      const timeoutId = setTimeout(() => abortRef.current?.abort(), REQUEST_TIMEOUT_MS)
      isFetchingRef.current = true
      setLoading(true)

      try {
        const params = { page_num: page, page_size: PAGE_SIZE }
        if (categoryId) params.categories_id = categoryId

        const { data } = await api.getDetailSubject(getAccessToken(), id, params, {
          signal: abortRef.current.signal
        })

        clearTimeout(timeoutId)

        const newItems = data?.videos || []

        // Page 1 → replace; page N → append
        setItems((prev) => (page === PAGE_NUM ? newItems : [...prev, ...newItems]))
        setTopicMeta(data)

        // No more pages when server returns fewer items than requested
        setHasMore(newItems.length >= PAGE_SIZE)
      } catch (err) {
        clearTimeout(timeoutId)
        if (err.name === "AbortError") return // intentionally cancelled — no-op
        console.error("[useTopicData] fetchPage error:", err)
        setHasMore(false)
      } finally {
        setLoading(false)
        isFetchingRef.current = false
      }
    },
    [id] // stable: only changes when the page id changes
  )

  // ── Effect: id navigation (SSR route change) ────────────────────────────────
  useEffect(() => {
    // Cancel anything in flight from the previous topic
    abortRef.current?.abort()
    clearTimeout(debounceRef.current)
    isFetchingRef.current = false

    // Hydrate from fresh SSR data
    setTopicMeta(initialTopic)
    setCategories(initialTopic?.categories || [])
    setItems(initialTopic?.videos || [])
    setHasMore((initialTopic?.videos?.length || 0) >= PAGE_SIZE)
    setPageNum(PAGE_NUM)
    setActiveCategory(null)

    // Mark that we still have SSR data — skip the first category effect run
    isSSRHydratedRef.current = false
  }, [id]) // intentionally omit initialTopic — we only want id-driven resets

  // ── Effect: category tab change (with debounce) ─────────────────────────────
  useEffect(() => {
    // First run after SSR hydration → skip fetch, data already in state
    if (!isSSRHydratedRef.current) {
      isSSRHydratedRef.current = true
      return
    }

    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      // Abort previous request before resetting UI
      abortRef.current?.abort()
      isFetchingRef.current = false

      setItems([])
      setHasMore(true)
      setPageNum(PAGE_NUM)

      // Use the ref so we always read the latest category value
      fetchPage(PAGE_NUM, activeCategoryRef.current)
    }, DEBOUNCE_MS)

    return () => clearTimeout(debounceRef.current)
  }, [activeCategory, fetchPage])

  // ── Effect: pagination (infinite scroll) ────────────────────────────────────
  useEffect(() => {
    if (pageNum > PAGE_NUM && hasMore) {
      fetchPage(pageNum, activeCategoryRef.current)
    }
  }, [pageNum, hasMore, fetchPage])

  // ── Effect: global cleanup on unmount ───────────────────────────────────────
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      clearTimeout(debounceRef.current)
      isFetchingRef.current = false
    }
  }, [])

  return {
    // topicMeta,
    // topicMeta: { ...topicMeta, categories },
    topicMeta,
    categories,
    items,
    loading,
    hasMore,
    activeCategory,
    setActiveCategory,
    pageNum,
    setPageNum,
    isFetchingRef
  }
}
